import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ExpandableSection,
  FormField,
  Multiselect,
  MultiselectProps,
  RadioGroup,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsReport } from "../../lusca-search-api/generated-src";

export type OmsColumnsDraft = {
  strategy: "all" | "selected";
  names: string[];
  touched?: boolean;
};

export function columnSelectionError(
  settings: OmsColumnsDraft,
  report: OmsReport
): string | undefined {
  if (settings.strategy === "all") return undefined;
  if (!settings.names.length) return "Choose at least one column.";
  const missing = settings.names.filter(
    (name) => !report.columns.includes(name)
  );
  if (missing.length)
    return `Unavailable in this source: ${missing.slice(0, 3).join(", ")}${
      missing.length > 3 ? ` and ${missing.length - 3} more` : ""
    }. Remove or replace these columns.`;
  if (new Set(settings.names).size !== settings.names.length)
    return "Remove duplicate output columns.";
  return undefined;
}

export default function OMSColumns({
  report,
  settings,
  onChange,
  expanded,
  onExpand,
  focusRequested = false,
  onFocusHandled,
}: {
  report: OmsReport;
  settings: OmsColumnsDraft;
  onChange: (value: OmsColumnsDraft) => void;
  expanded: boolean;
  onExpand: (expanded: boolean) => void;
  focusRequested?: boolean;
  onFocusHandled?: () => void;
}) {
  const picker = useRef<MultiselectProps.Ref>(null);
  const pickerContainer = useRef<HTMLDivElement>(null);
  const [showOrder, setShowOrder] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);
  const error = columnSelectionError(settings, report);
  const visibleError =
    error && (settings.touched || settings.names.length > 0)
      ? error
      : undefined;
  useEffect(() => {
    if (!focusRequested && !openPicker) return undefined;
    onExpand(true);
    const timer = window.setTimeout(() => {
      picker.current?.focus();
      const trigger = pickerContainer.current?.querySelector<HTMLButtonElement>(
        'button[aria-haspopup="dialog"], button[aria-haspopup="listbox"]'
      );
      if (openPicker && trigger?.getAttribute("aria-expanded") !== "true")
        trigger?.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      setOpenPicker(false);
      onFocusHandled?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [focusRequested, openPicker, onExpand, onFocusHandled]);

  function reorder(index: number, direction: number) {
    const names = [...settings.names];
    const target = index + direction;
    [names[index], names[target]] = [names[target], names[index]];
    onChange({ ...settings, names, touched: true });
  }
  return (
    <SpaceBetween size="xs">
      <ExpandableSection
        variant="container"
        headingTagOverride="h3"
        headerText="Columns"
        headerDescription={
          settings.strategy === "all"
            ? `All columns (${report.columns.length})`
            : `${settings.names.length} of ${report.columns.length} columns selected`
        }
        expanded={expanded}
        onChange={({ detail }) => onExpand(detail.expanded)}
      >
        <SpaceBetween size="m">
          <FormField
            label="Include columns"
            constraintText="All columns includes future fields. A selection keeps only the fields you choose."
          >
            <RadioGroup
              value={settings.strategy}
              items={[
                { value: "all", label: "All columns" },
                { value: "selected", label: "Selected columns" },
              ]}
              onChange={({ detail }) => {
                const strategy = detail.value as OmsColumnsDraft["strategy"];
                onChange({ ...settings, strategy });
                if (strategy === "selected") setOpenPicker(true);
              }}
            />
          </FormField>
          {settings.strategy === "selected" && (
            <>
              <FormField
                label="Output columns"
                errorText={visibleError}
                constraintText={
                  !visibleError && !settings.names.length
                    ? "Choose at least one column."
                    : undefined
                }
              >
                <div ref={pickerContainer}>
                  <Multiselect
                    ref={picker}
                    ariaLabel="Output columns"
                    filteringType="auto"
                    filteringPlaceholder="Search columns"
                    filteringAriaLabel="Search columns"
                    filteringClearAriaLabel="Clear column search"
                    placeholder="Choose columns"
                    empty="This report has no available columns."
                    noMatch="No matching columns."
                    tokenLimit={3}
                    i18nStrings={{
                      tokenLimitShowMore: "Show more chosen columns",
                      tokenLimitShowFewer: "Show fewer chosen columns",
                    }}
                    selectedOptions={settings.names.map((name) => ({
                      label: name,
                      value: name,
                    }))}
                    options={[...report.columns]
                      .sort((a, b) => a.localeCompare(b))
                      .map((name) => ({ label: name, value: name }))}
                    deselectAriaLabel={(option) =>
                      `Remove column ${
                        option.label || option.value || "column"
                      }`
                    }
                    onChange={({ detail }) =>
                      onChange({
                        ...settings,
                        names: detail.selectedOptions.map(
                          (option) => option.value as string
                        ),
                        touched: true,
                      })
                    }
                  />
                </div>
              </FormField>
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={() =>
                    onChange({
                      ...settings,
                      names: [...report.columns],
                      touched: true,
                    })
                  }
                >
                  Select all columns
                </Button>
                <Button
                  disabled={!settings.names.length}
                  onClick={() =>
                    onChange({ ...settings, names: [], touched: true })
                  }
                >
                  Clear selection
                </Button>
                <Button
                  disabled={!settings.names.length}
                  onClick={() => setShowOrder(!showOrder)}
                >
                  {showOrder ? "Hide column order" : "Column order"}
                </Button>
              </SpaceBetween>
              {showOrder && settings.names.length > 0 && (
                <SpaceBetween size="s">
                  <Box variant="h4">CSV column order</Box>
                  <ol className="oms-columns__order">
                    {settings.names.map((name, index) => (
                      <li key={name}>
                        <div className="oms-columns__order-row">
                          <span>{name}</span>
                          <SpaceBetween direction="horizontal" size="xxs">
                            <Button
                              iconName="angle-up"
                              ariaLabel={`Move ${name} up`}
                              disabled={index === 0}
                              onClick={() => reorder(index, -1)}
                            />
                            <Button
                              iconName="angle-down"
                              ariaLabel={`Move ${name} down`}
                              disabled={index === settings.names.length - 1}
                              onClick={() => reorder(index, 1)}
                            />
                          </SpaceBetween>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <Button
                    onClick={() =>
                      onChange({
                        ...settings,
                        names: [...settings.names].sort(
                          (a, b) =>
                            report.columns.indexOf(a) -
                            report.columns.indexOf(b)
                        ),
                      })
                    }
                  >
                    Reset to source order
                  </Button>
                </SpaceBetween>
              )}
            </>
          )}
        </SpaceBetween>
      </ExpandableSection>
      {visibleError && !expanded && (
        <div role="alert">
          <Box color="text-status-error">{visibleError}</Box>
        </div>
      )}
    </SpaceBetween>
  );
}
